"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PLATFORM_ASPECT_RATIO_SPECS = void 0;
exports.getPlatformAspectRatioSpec = getPlatformAspectRatioSpec;
exports.matchAllowedAspectRatio = matchAllowedAspectRatio;
exports.isAspectRatioAllowed = isAspectRatioAllowed;
exports.formatAllowedAspectRatios = formatAllowedAspectRatios;
const RATIO_9_16 = { label: "9:16", width: 9, height: 16 };
const RATIO_4_5 = { label: "4:5", width: 4, height: 5 };
const RATIO_1_1 = { label: "1:1", width: 1, height: 1 };
const RATIO_16_9 = { label: "16:9", width: 16, height: 9 };
const RATIO_191_100 = {
    label: "1.91:1",
    width: 191,
    height: 100,
};
const RATIO_9_20 = { label: "9:20", width: 9, height: 20 };
const RATIO_20_9 = { label: "20:9", width: 20, height: 9 };
exports.PLATFORM_ASPECT_RATIO_SPECS = {
    META: {
        // Meta's API accepts the full 9:16–16:9 band for feed video (taller than
        // 4:5 is masked in feed but still delivers to Stories/Reels), so a range
        // is the accurate model even though our multi-aspect launches target the
        // discrete 9:16 / 4:5 / 1:1 set.
        video: { kind: "range", min: RATIO_9_16, max: RATIO_16_9 },
        image: { kind: "range", min: RATIO_9_16, max: RATIO_191_100 },
    },
    TIKTOK: {
        video: {
            kind: "discrete",
            allowed: [RATIO_9_16, RATIO_1_1, RATIO_16_9],
            tolerance: 0.03,
        },
        // Carousel/in-feed images; mirrors platform-sdk tiktok/validation/imageSpecs.
        image: { kind: "range", min: RATIO_9_20, max: RATIO_20_9 },
    },
    SNAPCHAT: {
        video: { kind: "discrete", allowed: [RATIO_9_16], tolerance: 0.01 },
        image: { kind: "discrete", allowed: [RATIO_9_16], tolerance: 0.01 },
    },
    GOOGLE: {
        // Demand Gen. Videos are YouTube-hosted; images map to the landscape /
        // square / portrait / Shorts slots (5% tolerance matches the backend's
        // Demand Gen image classifier).
        video: {
            kind: "discrete",
            allowed: [RATIO_16_9, RATIO_1_1, RATIO_9_16],
            tolerance: 0.05,
        },
        image: {
            kind: "discrete",
            allowed: [RATIO_191_100, RATIO_1_1, RATIO_4_5, RATIO_9_16],
            tolerance: 0.05,
        },
    },
};
function getPlatformAspectRatioSpec(platform, mediaKind) {
    // Read through the registry type so a platform that declares no specs is a
    // miss rather than a compile error at every call site.
    const registry = exports.PLATFORM_ASPECT_RATIO_SPECS;
    const spec = registry[platform];
    if (!spec) {
        // Reporting-only platforms declare no creative specs because nothing is
        // ever uploaded to them. Callers reach this only by asking a launch
        // question about a platform that cannot launch.
        throw new Error(`No aspect-ratio specification for platform ${platform}`);
    }
    return spec[mediaKind];
}
function quotient(ratio) {
    return ratio.width / ratio.height;
}
/**
 * Returns the allowed ratio a width/height pair matches, or null when the
 * platform would not accept it. For range specs the returned ratio is the
 * nearest bound label; callers wanting a display label for arbitrary
 * in-range media should compute their own.
 */
function matchAllowedAspectRatio(spec, width, height) {
    if (!Number.isFinite(width) ||
        !Number.isFinite(height) ||
        height <= 0 ||
        width <= 0) {
        return null;
    }
    const actual = width / height;
    if (spec.kind === "range") {
        const min = quotient(spec.min);
        const max = quotient(spec.max);
        if (actual < min || actual > max)
            return null;
        return actual - min <= max - actual ? spec.min : spec.max;
    }
    let best = null;
    let bestDelta = Number.POSITIVE_INFINITY;
    for (const candidate of spec.allowed) {
        const target = quotient(candidate);
        const delta = Math.abs(actual - target) / target;
        if (delta <= spec.tolerance && delta < bestDelta) {
            best = candidate;
            bestDelta = delta;
        }
    }
    return best;
}
function isAspectRatioAllowed(spec, width, height) {
    return matchAllowedAspectRatio(spec, width, height) !== null;
}
/** Formats the allowed set for user-facing copy, e.g. "9:16, 1:1 or 16:9". */
function formatAllowedAspectRatios(spec) {
    if (spec.kind === "range") {
        return `between ${spec.min.label} and ${spec.max.label}`;
    }
    const labels = spec.allowed.map((ratio) => ratio.label);
    if (labels.length === 1)
        return labels[0];
    return `${labels.slice(0, -1).join(", ")} or ${labels[labels.length - 1]}`;
}
